/**
 * Toast Context - Custom toast notifications with Tailwind
 */
import React, { createContext, useContext, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const ToastContext = createContext(null);

/**
 * Normalize ``showToast`` call sites:
 *
 * - Legacy: ``showToast('Saved', 'success')``
 * - Structured: ``showToast({ type, title?, message })`` — title/body flattened to text
 *
 * Passing a structured payload as ``message`` used to poison ``toast.message`` with
 * a non-React child object (minified invariant #31) and blew up the shell after deletes.
 */
function normalizeToastPayload(messageOrOpts, maybeType = 'success') {
  let type =
    typeof maybeType === 'string' && maybeType ? maybeType : 'success';

  if (
    messageOrOpts !== null &&
    typeof messageOrOpts === 'object' &&
    !(messageOrOpts instanceof Error) &&
    !Array.isArray(messageOrOpts) &&
    typeof messageOrOpts.type === 'string'
  ) {
    type = messageOrOpts.type || type;
    const title =
      typeof messageOrOpts.title === 'string'
        ? messageOrOpts.title.trim()
        : '';
    const rawBody = messageOrOpts.message;
    const body =
      typeof rawBody === 'string'
        ? rawBody
        : rawBody != null && rawBody !== ''
          ? String(rawBody)
          : '';
    const message =
      title && body ? `${title}: ${body}` : title || body || 'Done';
    return { message, type };
  }

  if (messageOrOpts instanceof Error) {
    return { message: messageOrOpts.message || 'Error', type };
  }

  const message =
    messageOrOpts == null
      ? ''
      : typeof messageOrOpts === 'string'
        ? messageOrOpts
        : String(messageOrOpts);

  return { message: message || 'Done', type };
}

export function ToastProvider({ children }) {
  const [toast, setToast] = useState(null);

  const showToast = useCallback((messageOrOpts, maybeType = 'success') => {
    const { message, type } = normalizeToastPayload(messageOrOpts, maybeType);
    setToast({ message, type });
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, []);

  const dismiss = useCallback(() => setToast(null), []);

  return (
    <ToastContext.Provider value={{ showToast, dismiss }}>
      {children}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            className="fixed top-4 right-4 z-50"
          >
            <div
              className={`px-4 py-3 rounded-lg shadow-lg border text-sm font-medium flex items-center gap-2 ${
                toast.type === 'success'
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                  : toast.type === 'error'
                  ? 'bg-red-50 border-red-200 text-red-800'
                  : toast.type === 'info'
                  ? 'bg-sky-50 border-sky-200 text-sky-900'
                  : 'bg-gray-50 border-gray-200 text-gray-800'
              }`}
            >
              {toast.type === 'success' && (
                <svg className="w-4 h-4 text-emerald-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              )}
              {toast.type === 'error' && (
                <svg className="w-4 h-4 text-red-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
              <span>{toast.message}</span>
              <button
                onClick={dismiss}
                className="ml-2 p-0.5 rounded hover:bg-black/5 -mr-0.5"
                aria-label="Dismiss"
              >
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
